// Stale-chunk recovery for the lazy-loaded SPA.
//
// After a fresh deployment the old hashed JS chunks referenced by a cached
// index.html no longer exist on the server. A lazy `import()` for one of those
// chunks then fails with "Failed to fetch dynamically imported module" (or the
// server returns the SPA shell HTML, yielding "'text/html' is not a valid
// JavaScript MIME type"), and the resulting partial/garbled modules surface as
// secondary crashes (e.g. AnimatePresence is not defined, React null useState).
//
// The cure is the same in every case: force a full-page reload so the browser
// re-fetches the current index.html and its current chunk graph. A session
// flag prevents an infinite reload loop if the failure persists post-reload.

const RELOAD_FLAG = "bzr_chunk_reloaded";

const CHUNK_ERROR_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Importing a module script failed",
  "is not a valid JavaScript MIME type",
  "Unable to preload CSS",
  "ChunkLoadError",
];

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  if (typeof error === "object" && "name" in error && (error as { name?: unknown }).name === "ChunkLoadError") {
    return true;
  }
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : typeof error === "object" && "message" in error
          ? String((error as { message?: unknown }).message ?? "")
          : "";
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

// Reload once. Returns true if a reload was triggered, false if we already
// reloaded this session (so the caller can surface a real error instead).
export function reloadForChunkError(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return false;
    sessionStorage.setItem(RELOAD_FLAG, "1");
  } catch {
    // sessionStorage unavailable (private mode / blocked) — reload anyway; the
    // worst case is a single extra reload, which still recovers the page.
  }
  window.location.reload();
  return true;
}

// Clear the guard flag once the app has successfully mounted, so a *future*
// stale-chunk failure in the same tab can reload again.
export function clearChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* ignore */
  }
}

// Wire up global listeners for chunk failures that escape React (e.g. a failed
// import() whose rejection is never caught, or a script load error event).
export function installChunkReloadHandler(): void {
  window.addEventListener("error", (event) => {
    if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
      reloadForChunkError();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      reloadForChunkError();
    }
  });
}
